import { ReportComment } from '@helloself/hello-self-ui';
import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { createPartnerReportComment } from '../../../../api/comments/comments';
import { CreatePartnerReportCommentRequest, CreatePartnerReportCommentResponse } from '../../../../api/comments/types';
import { AppDispatch, RootState } from '../../../../app/store';
import { emptyReturnObject, RequestStatusWithDetails } from '../../../../types/RequestStatusWithDetails';
import { ReportsSlice } from '../slice';

export const asyncAddReportComment = createAsyncThunk<
    CreatePartnerReportCommentResponse,
    { reportId: number; request: CreatePartnerReportCommentRequest },
    {
        dispatch: AppDispatch;
        state: RootState;
    }
>('reports/addComment', async (args) => {
    return await createPartnerReportComment(args.reportId, args.request);
});

const reducer = (builder: ActionReducerMapBuilder<ReportsSlice>) => {
    builder.addCase(asyncAddReportComment.pending, (state, action) => {
        const existingCommentsState = state.reportComments[action.meta.arg.reportId] ?? emptyReturnObject();
        state.reportComments[action.meta.arg.reportId] = {
            ...existingCommentsState,
            status: existingCommentsState.loadedTimestamp ? 'reloading' : 'pending',
        } as RequestStatusWithDetails<ReportComment[]>;
    });

    builder.addCase(asyncAddReportComment.rejected, (state, action) => {
        state.reportComments[action.meta.arg.reportId].status = 'error';
    });

    builder.addCase(asyncAddReportComment.fulfilled, (state, action) => {
        const comments = action.payload.comments;

        // Create a set to store unique postedTime values
        const uniquePostedTimes = new Set();

        // Filter out objects with duplicate postedTime values
        const filteredData = comments.filter((item) => {
            if (!uniquePostedTimes.has(item.postedTime)) {
                uniquePostedTimes.add(item.postedTime);
                return true;
            }
            return false;
        });

        state.reportComments[action.meta.arg.reportId] = {
            status: 'success',
            loadedTimestamp: Date.now(),
            data: filteredData,
        };
    });
};

export default reducer;
